import { VECTOR_SCALE } from '@kenocyber/game-domain/pvp';
import type {
  PvpAppearance,
  PvpCombatProfileWire,
  PvpRuneWireKind,
  PvpSeat,
} from '@kenocyber/shared';
import type { PvpArena } from './connection';
import { PVP_RUNE_PALETTES } from './rune-appearance';
import type {
  PvpParticipantId,
  PvpRenderFrame,
  PvpRenderMissile,
  PvpRenderProjectile,
  PvpRenderShip,
} from './view';

/**
 * Every decision the duel scene makes, with no Phaser in it.
 *
 * ## Why this module exists
 *
 * The same reason `starfield.ts`, `combat-text.ts` and `projectile-art.ts`
 * exist beside their scenes: what a renderer gets WRONG is arithmetic and
 * bookkeeping - a seat swapped, a scale off by a thousand, a projectile that
 * outlives the frame that described it - and none of that needs a WebGL context
 * to be proven. The scene is left as wiring, which is the part a browser test
 * is actually good at.
 *
 * Nothing here creates a game object, and nothing here decides a duel.
 */

/* -------------------------------------------------------------------------- */
/*  Scale                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Milli-units per arena unit, taken from the domain rather than chosen here.
 *
 * The server's own balance file states the conversion out loud: a ship moves
 * `PVP_SHIP_SPEED_MU_PER_TICK` = 17 000 mu per tick at 20 Hz, which its comment
 * calls "340 arena units per second". A thousand milli-units is one unit, and
 * this constant is that sentence rather than a second scale system invented for
 * the client.
 *
 * So the arena is 4800 x 3000 units, a hull is 26 across and a shot is 7 - all
 * of them read off the welcome and divided by this, never hard-coded.
 */
export const PVP_MU_PER_UNIT = 1_000;

/** Server milli-units to the world coordinates the camera works in. */
export function pvpWorldUnits(milliUnits: number): number {
  return milliUnits / PVP_MU_PER_UNIT;
}

/**
 * The arena, in world units, exactly as the welcome described it.
 *
 * Nothing is added: an obstacle the server did not send is not drawn, and the
 * bounds are the server's bounds. A client that decorated the arena with a
 * pillar of its own would be showing cover that stops no shots.
 */
export interface PvpWorldArena {
  readonly width: number;
  readonly height: number;
  readonly obstacles: ReadonlyArray<{
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  }>;
  readonly shipRadius: number;
  readonly projectileRadius: number;
  /**
   * What each seat looks like, carried through unconverted.
   *
   * A profile is catalogue indices and a rarity, not a length, so there is
   * nothing here to divide by {@link PVP_MU_PER_UNIT}. It rides along so the
   * scene has one object to read the arena from.
   */
  /**
   * What each participant looks like, keyed by participant id.
   *
   * A LOOKUP rather than the `{a, b}` pair it came from, so a room with more
   * than two seats needs no new shape here. The welcome's own object satisfies
   * this structurally - its keys are `a` and `b`, which are exactly the
   * participant ids of a 1x1.
   */
  readonly appearance: Readonly<Record<string, PvpAppearance | null | undefined>>;
  /**
   * O nickname de cada participante, pela mesma chave (gate V28H.R).
   *
   * Uma das duas coisas que dizem de quem é uma nave, com a barra de vida. A
   * cor dos efeitos de carta deixou de o dizer: ela é agora a escolha de quem
   * olha, e vale para todos.
   */
  readonly nickname: Readonly<Record<string, string | null | undefined>>;
  /** Each hull's four numbers, keyed by participant id. Copied straight through. */
  readonly combatProfile: Readonly<Record<string, PvpCombatProfileWire | undefined>>;
  /** The normalised reference, for `pvpVisualScale`. Never a bar's denominator. */
  /** A referência normalizada da métrica do casco, em MILLI. Nunca uma barra. */
  readonly maxHealthMilli: number;
}

export function pvpWorldArena(arena: PvpArena): PvpWorldArena {
  return {
    width: pvpWorldUnits(arena.width),
    height: pvpWorldUnits(arena.height),
    obstacles: arena.obstacles.map((obstacle) => ({
      x: pvpWorldUnits(obstacle.x),
      y: pvpWorldUnits(obstacle.y),
      w: pvpWorldUnits(obstacle.w),
      h: pvpWorldUnits(obstacle.h),
    })),
    shipRadius: pvpWorldUnits(arena.shipRadius),
    projectileRadius: pvpWorldUnits(arena.projectileRadius),
    appearance: arena.appearance,
    nickname: arena.nickname,
    combatProfile: arena.combatProfile,
    maxHealthMilli: arena.maxHealthMilli,
  };
}

/* -------------------------------------------------------------------------- */
/*  Rotation                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The heading a normalised aim vector describes, in radians.
 *
 * The components arrive as fixed point at {@link VECTOR_SCALE}, so `65536, 0`
 * is due right. The scale cancels in `atan2`, which is why this can be handed
 * raw components without dividing first - and why a client cannot accidentally
 * change an angle by changing a zoom.
 *
 * A zero vector has no heading. The caller keeps whatever it was drawing rather
 * than snapping a hull to due right, which is what `atan2(0, 0)` would give.
 */
export function pvpHeading(ax: number, ay: number): number | null {
  if (!Number.isFinite(ax) || !Number.isFinite(ay) || (ax === 0 && ay === 0)) {
    return null;
  }
  return Math.atan2(ay, ax);
}

/**
 * Which way a projectile is pointing.
 *
 * Derived from where it MOVED between two authoritative samples, and from
 * nothing else. Never from where the other ship is: a duel shot does not home,
 * does not keep a target and must not even be drawn as though it might.
 *
 * A shot that has not moved yet - its first frame - has no derived heading, and
 * the caller falls back to the firing ship's aim rather than inventing one.
 */
export function pvpProjectileHeading(
  from: { readonly x: number; readonly y: number } | undefined,
  to: { readonly x: number; readonly y: number },
): number | null {
  if (!from) {
    return null;
  }
  return pvpHeading(to.x - from.x, to.y - from.y);
}

/* -------------------------------------------------------------------------- */
/*  Seats                                                                     */
/* -------------------------------------------------------------------------- */

/** The two ships, told apart by the seat the SERVER assigned this browser. */
export interface PvpRoster {
  readonly mine: PvpRenderShip | null;
  readonly theirs: PvpRenderShip | null;
}

/**
 * Sorts a frame's ships into mine and theirs.
 *
 * The local seat comes from the welcome and is passed in; it is never guessed
 * from an order, an index or a position. Getting this backwards would point the
 * camera at the opponent and colour the wrong hull as the player's, and both
 * look plausible enough on screen to survive a glance.
 */
export function pvpRoster(frame: PvpRenderFrame, seat: PvpParticipantId | null): PvpRoster {
  if (seat === null) {
    return { mine: null, theirs: null };
  }
  return {
    mine: frame.ships.find((ship) => ship.seat === seat) ?? null,
    theirs: frame.ships.find((ship) => ship.seat !== seat) ?? null,
  };
}

/* -------------------------------------------------------------------------- */
/*  Health                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * How full a health bar is, as a fraction of the maximum the server stated.
 *
 * Clamped at both ends. Health cannot be negative and cannot exceed the
 * maximum, and a bar that drew either would be drawing a number the room never
 * reported - which is exactly what happens if a client subtracts its own idea
 * of damage from its own idea of a starting value.
 *
 * Not smoothed. Damage is discrete, so the bar is allowed to jump; sliding it
 * would show a ship alive for a moment after the room says it is not.
 */
export function pvpHealthFraction(healthMilli: bigint, maxHealthMilli: bigint): number {
  if (maxHealthMilli <= 0n) {
    return 0;
  }
  if (healthMilli <= 0n) {
    return 0;
  }
  if (healthMilli >= maxHealthMilli) {
    return 1;
  }
  /*
   * A razão EXATA, em milli, e uma divisão só (gate V28G.S.1).
   *
   * Escalada por um milhão em `bigint` antes de virar `number`, para que uma
   * vida de dezassete dígitos produza a mesma fração que uma de seis - e nunca
   * um `Infinity` nem um zero por um `double` ter perdido a magnitude.
   */
  return Number((healthMilli * 1_000_000n) / maxHealthMilli) / 1_000_000;
}

/* -------------------------------------------------------------------------- */
/*  Projectiles                                                               */
/* -------------------------------------------------------------------------- */

/**
 * What changed between what is on screen and what the server just described.
 *
 * The whole point is `remove`: a projectile absent from the newest snapshot is
 * gone THIS frame - it hit something, expired, or the match ended - and fading
 * it out would leave something on screen that looks like a live shot and can no
 * longer do anything.
 */
export interface PvpProjectileDiff {
  readonly spawn: readonly PvpRenderProjectile[];
  readonly update: readonly PvpRenderProjectile[];
  readonly remove: readonly number[];
}

/**
 * Diffs the drawn projectile ids against a frame.
 *
 * Identity is the SERVER's id and nothing else - not an index, not a position,
 * not a slot in a pool. An index would reassign a sprite to a different shot
 * the moment one in the middle of the list disappeared, which is a shot
 * teleporting across the arena.
 */
export function pvpProjectileDiff(
  drawn: Iterable<number>,
  frame: PvpRenderFrame,
): PvpProjectileDiff {
  const present = new Set<number>();
  const spawn: PvpRenderProjectile[] = [];
  const update: PvpRenderProjectile[] = [];
  const known = drawn instanceof Set ? drawn : new Set(drawn);

  for (const projectile of frame.projectiles) {
    present.add(projectile.id);
    if (known.has(projectile.id)) {
      update.push(projectile);
    } else {
      spawn.push(projectile);
    }
  }
  const remove: number[] = [];
  for (const id of known) {
    if (!present.has(id)) {
      remove.push(id);
    }
  }
  return { spawn, update, remove };
}

/**
 * O mesmo diff, para os MÍSSEIS (gate V28G.L).
 *
 * ## Porque uma segunda função em vez de uma genérica
 *
 * Porque os dois tipos não têm um supertipo útil - um projétil carrega `bomb` e
 * um míssil carrega `heading` - e generalizá-los obrigaria a inventar um. O
 * corpo é o mesmo algoritmo sobre uma lista diferente, e a duplicação é de seis
 * linhas de laço contra um tipo que ninguém quer.
 *
 * `remove` significa o mesmo e pela mesma razão: um míssil ausente da instantânea
 * mais nova acabou - acertou, expirou, tocou uma parede ou o limite, ou a partida
 * terminou. A cena NÃO desenha uma explosão aqui; quem anuncia uma explosão é a
 * autoridade, pelo canal `pvp:blast`.
 */
export interface PvpMissileDiff {
  readonly spawn: readonly PvpRenderMissile[];
  readonly update: readonly PvpRenderMissile[];
  readonly remove: readonly number[];
}

export function pvpMissileDiff(drawn: Iterable<number>, frame: PvpRenderFrame): PvpMissileDiff {
  const present = new Set<number>();
  const spawn: PvpRenderMissile[] = [];
  const update: PvpRenderMissile[] = [];
  const known = drawn instanceof Set ? drawn : new Set(drawn);

  for (const missile of frame.missiles) {
    present.add(missile.id);
    if (known.has(missile.id)) {
      update.push(missile);
    } else {
      spawn.push(missile);
    }
  }
  const remove: number[] = [];
  for (const id of known) {
    if (!present.has(id)) {
      remove.push(id);
    }
  }
  return { spawn, update, remove };
}

/* -------------------------------------------------------------------------- */
/*  Corrections                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Notices that the world was REPLACED rather than advanced.
 *
 * A welcome, or a tick that went backwards because the room was rebuilt from a
 * snapshot up to a second old. Everything drawn describes a world that no
 * longer follows from this one, so trails, tweens and every projectile go -
 * and the new snapshot is applied whole, immediately, without a single frame of
 * sliding between two worlds that were never continuous.
 *
 * A small object rather than a bare number so the caller cannot forget to
 * update its own copy: `seen` is what it must store back.
 */
export class PvpCorrectionWatch {
  private lastSeen = 0;

  /** Whether this frame is a discontinuity that must clear the scene. */
  observe(correction: number): boolean {
    if (correction === this.lastSeen) {
      return false;
    }
    this.lastSeen = correction;
    return true;
  }

  reset(): void {
    this.lastSeen = 0;
  }
}

/* -------------------------------------------------------------------------- */
/*  Camera                                                                    */
/* -------------------------------------------------------------------------- */

/** How hard the camera pulls towards the local ship, per frame at 60 Hz. */
export const PVP_CAMERA_LERP = 0.12;

/**
 * Where the camera may look.
 *
 * The arena's own box, in world units. Phaser clamps a bounded camera itself,
 * so the only thing that can go wrong here is handing it a box that is not the
 * arena - which would either show a band of nothing outside the wall or hide
 * part of the floor a ship can stand on.
 */
/**
 * How far OUTSIDE the arena the camera may look, in world units.
 *
 * ## Why the camera is allowed out of the box at all
 *
 * The arena is 4800x3000 world units and the minimum zoom is 0.4, so a
 * 1920-wide viewport sees exactly the arena's width and no more. Exactly is the
 * problem: a camera clamped to the walls has nowhere left to move sideways, so
 * it cannot put the ship in the middle of the screen anywhere except dead
 * centre. Measured on the player's own recording, back when the arena was
 * narrower than one screen: the hull sat 244 px right of centre for four
 * seconds while standing still, which is the clamp, not lag.
 *
 * Widening the arena further would change collisions and balance. Narrowing the
 * zoom range would change how the duel reads. So the PLAYABLE box and the
 * VISIBLE box are separated instead: the ship still cannot leave the arena, and
 * the camera still can. What is out there is drawn as empty space and is
 * unmistakably not part of the map.
 *
 * ## Why the margin is computed rather than written down
 *
 * A number chosen for 1920x1080 is wrong on a wider monitor, and a number
 * chosen for the widest monitor anybody has today is wrong next year. The
 * margin needed to centre a hull standing exactly on a wall is half of what the
 * viewport can see, and what it can see is `viewport / zoom`. So the margin is
 * derived from the CURRENT viewport at the LOWEST zoom, and recomputed whenever
 * either changes - which is what `resize` and the wheel handler do.
 *
 * There is no official maximum supported resolution in this project, so none is
 * assumed: the arithmetic works for any viewport, and an 8K display simply
 * produces a larger margin.
 */
export function pvpVisualMargin(
  viewportWidth: number,
  viewportHeight: number,
  minZoom: number,
): { readonly x: number; readonly y: number } {
  const zoom = minZoom > 0 ? minZoom : 1;
  /*
   * Half a viewport, plus a little.
   *
   * Half is exactly what centring a hull on the wall needs. The extra tenth
   * keeps the very edge of the visible area from landing on the wall itself at
   * awkward aspect ratios, where a rounding of one pixel would clamp the camera
   * and put the ship back off-centre.
   */
  return {
    x: Math.ceil((viewportWidth / zoom / 2) * 1.1),
    y: Math.ceil((viewportHeight / zoom / 2) * 1.1),
  };
}

/**
 * The box the CAMERA may move in - the arena plus the visual margin.
 *
 * Not the box the ship may move in. Nothing here is navigable: the room clamps
 * every hull to the arena and knows nothing about this.
 */
export function pvpCameraBounds(
  arena: PvpWorldArena,
  viewportWidth: number,
  viewportHeight: number,
  minZoom: number,
): {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
} {
  const margin = pvpVisualMargin(viewportWidth, viewportHeight, minZoom);
  return {
    x: -margin.x,
    y: -margin.y,
    width: arena.width + margin.x * 2,
    height: arena.height + margin.y * 2,
  };
}

/**
 * Where the camera should be pointed this frame.
 *
 * `null` when the local ship is not in the frame yet - before the first state
 * frame, or between a welcome and the snapshot that follows it. The caller then
 * leaves the camera where it is rather than following the OPPONENT, which is
 * what "the first ship in the list" would quietly do.
 */
export function pvpCameraTarget(
  frame: PvpRenderFrame | null,
  seat: PvpParticipantId | null,
): { readonly x: number; readonly y: number } | null {
  if (!frame) {
    return null;
  }
  const mine = pvpRoster(frame, seat).mine;
  if (!mine) {
    return null;
  }
  return { x: pvpWorldUnits(mine.x), y: pvpWorldUnits(mine.y) };
}

/**
 * The camera's resting place before anything authoritative has arrived.
 *
 * The middle of the arena. Not the origin, which is a corner and would show a
 * quarter of the floor and three quarters of nothing.
 */
export function pvpCameraHome(arena: PvpWorldArena): {
  readonly x: number;
  readonly y: number;
} {
  return { x: arena.width / 2, y: arena.height / 2 };
}

/* -------------------------------------------------------------------------- */
/*  Seat colours                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Which hull is the player's.
 *
 * By SEAT, never by position, so the two clients in one duel disagree about
 * which is which - correctly. Distinct enough to tell apart at the smallest
 * zoom, and not red: red is the health bar's, on both ships, and a hull sharing
 * it would make a full bar look like part of the ship.
 */
export const PVP_OWN_TINT = 0x4ce0ff;
export const PVP_ENEMY_TINT = 0xff8a3d;

export function pvpSeatTint(shipSeat: PvpSeat, localSeat: PvpSeat | null): number {
  return shipSeat === localSeat ? PVP_OWN_TINT : PVP_ENEMY_TINT;
}

/* -------------------------------------------------------------------------- */
/*  Participants                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Who a ship is to the person looking at it.
 *
 * Three answers, not two, because a duel with teams has a middle one. In a 1x1
 * the middle is unreachable - there is nobody on your side - so `pvpSeatTint`
 * above and `pvpRoleTint` below agree exactly, which is what keeps the
 * two-player look unchanged.
 */
export type PvpParticipantRole = 'local' | 'ally' | 'enemy';

/**
 * A participant, identified explicitly.
 *
 * ## Why an id and not an array index
 *
 * A frame is a list, and a list reorders. Keying sprites by position means that
 * the moment the server sends its ships in another order - or one of them dies
 * and is dropped - every hull inherits somebody else's trail, heading and
 * health bar. The id is what the renderer follows; the array is only how it
 * arrived.
 *
 * In a 1x1 the id IS the seat, so nothing about the existing duel changes.
 */
export interface PvpParticipant {
  readonly participantId: PvpParticipantId;
  readonly team: PvpParticipantId;
  /** 1-based within the team. Presentation only: it decides no rule. */
  readonly slot: number;
  readonly isLocal: boolean;
}

/**
 * Just enough to answer "who is this to me": an id and a side.
 *
 * Both come from the AUTHORITY. Neither is ever derived from the other, from a
 * position, or from where in a list somebody arrived.
 */
export interface PvpIdentity {
  readonly participantId: PvpParticipantId;
  readonly team: PvpParticipantId;
}

/** What this participant is to the local player. */
export function pvpParticipantRole(
  participant: PvpIdentity,
  local: PvpIdentity | null,
): PvpParticipantRole {
  if (local === null) {
    // Before the server has said which seat this browser is, nobody is an ally.
    return 'enemy';
  }
  if (participant.participantId === local.participantId) {
    return 'local';
  }
  return participant.team === local.team ? 'ally' : 'enemy';
}

/**
 * An ally's colour.
 *
 * Between the two that already exist, and closer to the player's own: a
 * team-mate has to read as "with me" at a glance and still never be mistaken
 * for the ship the camera is following.
 */
export const PVP_ALLY_TINT = 0x62f0c8;

export function pvpRoleTint(role: PvpParticipantRole): number {
  switch (role) {
    case 'local':
      return PVP_OWN_TINT;
    case 'ally':
      return PVP_ALLY_TINT;
    default:
      return PVP_ENEMY_TINT;
  }
}

/* -------------------------------------------------------------------------- */
/*  Team identity                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The mark that says whose a ship is, and keeps saying it.
 *
 * ## Why the tint was not enough
 *
 * The hull tint used to be the only answer, and a rune OVERRIDES it: a ship
 * carrying Double Damage goes red and one carrying Speed goes green, on purpose,
 * because "that one is hitting twice as hard" is worth reading at a glance. With
 * one opponent that costs nothing - there is one other ship and the camera is on
 * yours. The six-participant capture showed what it costs with five: four of the
 * six hulls were wearing a rune colour, and the only thing still saying who was
 * who was the COLOUR OF THE FIRE. A player who stops shooting must not stop
 * being able to tell a team-mate from an enemy.
 *
 * ## Why it is a SHAPE and not another colour
 *
 * Three tints are three tints, and roughly one man in twelve cannot separate the
 * orange from the green. So each role gets its own geometry - two rings, one
 * ring, four corner brackets - and the colour is a second, redundant channel.
 * The mark is legible in greyscale.
 *
 * ## What it must never do
 *
 * Cover the health bar, turn with the cannon, replace the hull's art, or cost an
 * allocation per frame. It is drawn once per role and size, and after that it is
 * only moved.
 */
export type PvpIdentityShape =
  /** Concentric outlines. Two for the local ship, one for a team-mate. */
  | { readonly kind: 'rings'; readonly radii: readonly number[] }
  /**
   * Short arcs on the diagonals, and for the local ship a tick below it
   * (gate V27A).
   *
   * ## Why the full circles went
   *
   * A closed outline around a ship reads as a boundary - a shield, a hitbox, an
   * area of effect - and the player said exactly that about the double ring:
   * "parece escudo, hitbox ou área de colisão", and that it dominated every
   * skin. The information it carried is real and stays; the shape it carried it
   * in was the problem.
   *
   * ## How the three roles stay apart without colour
   *
   * Not by curvature alone, which is the trap: at the minimum zoom a curved
   * mark and an angular one are both a few pixels. So the discriminator is
   * something a small mark keeps -
   *
   * - **local**: four arcs AND a filled tick below the hull. The tick is unique
   *   to the ship the camera follows, so "is that mine" is one glance at one
   *   feature rather than a count.
   * - **ally**: two arcs, left and right, no tick.
   * - **opponent**: four angular brackets, unchanged.
   *
   * Four-with-a-tick, two, and four-angular are three different answers at any
   * size, and none of them needs a hue.
   */
  | {
      readonly kind: 'arcs';
      readonly radius: number;
      /** Half the angular width of each arc, in radians. */
      readonly sweep: number;
      /** Where each arc is centred, in radians. Screen axes; never rotated. */
      readonly angles: readonly number[];
      /** The local ship's own tick, below the hull. Null for everyone else. */
      readonly tick: { readonly radius: number; readonly halfWidth: number } | null;
    }
  /** Four corner brackets - unmistakably angular against a round ring. */
  | { readonly kind: 'brackets'; readonly radius: number; readonly arm: number };

export interface PvpIdentityMark {
  readonly shape: PvpIdentityShape;
  readonly colour: number;
  readonly lineWidth: number;
  readonly alpha: number;
}

/**
 * How far out the mark sits, as a fraction of the hull's own DIAMETER.
 *
 * Just outside the art, so it frames the ship rather than crossing it, and well
 * inside the health bar's lift - the bar floats 46 units up at 1x and this
 * reaches 30, so the two never touch at any drawn size.
 */
const IDENTITY_RADIUS = 0.58;

/**
 * Half the angular width of one arc, in radians.
 *
 * 0.34 is a shade under forty degrees, so four arcs cover about 43% of the
 * circle and leave the rest open. Wide enough to read as a deliberate mark at
 * the minimum zoom, open enough that it can never be mistaken for a boundary.
 */
const IDENTITY_ARC_SWEEP = 0.34;
/** The diagonals, so no arc sits where the health bar or the nose is. */
const IDENTITY_ARC_DIAGONALS: readonly number[] = Object.freeze([
  Math.PI / 4,
  (3 * Math.PI) / 4,
  (5 * Math.PI) / 4,
  (7 * Math.PI) / 4,
]);
/** Left and right only, for an ally. */
const IDENTITY_ARC_SIDES: readonly number[] = Object.freeze([0, Math.PI]);
/** How far below the hull the local tick sits, as a fraction of the radius. */
const IDENTITY_TICK = 1.02;
/** And how wide it is. Small: a tick, not a bar. */
const IDENTITY_TICK_HALF = 0.16;

/**
 * How far a health bar floats above its ship, at 1x. MULTIPLIED by the scale.
 *
 * Here rather than in the scene because the team mark has to be provably clear
 * of it: a fixed lift is legible on a 52-unit hull and sits ON TOP of a
 * 156-unit one, which is how it was found, and a mark that grew into the bar
 * would repeat the mistake one layer up. `identity.test.ts` asserts the gap at
 * every drawn size.
 */
export const PVP_BAR_LIFT = 46;
/**
 * The bar's own rectangle, in world units, and deliberately NOT scaled.
 *
 * A ship three times the size does not deserve a health bar three times as
 * wide; it deserves the same bar further out of the way, which is what the lift
 * above does. Here rather than in the scene for the same reason the lift is:
 * `rune-indicator.test.ts` proves what the rune marks may and may not reach, and
 * a second copy of 70x8 would eventually disagree with the one being drawn.
 */
export const PVP_BAR_WIDTH = 70;
export const PVP_BAR_HEIGHT = 8;
/** How long each enemy bracket's arm is, as a fraction of the radius. */
const IDENTITY_ARM = 0.42;

/**
 * The stroke width, which deliberately does NOT grow with the ship.
 *
 * A 3x hull with a 3x outline reads as a thick doughnut; the mark is meant to be
 * a hairline that states a fact. It is a world-unit width, so the camera's zoom
 * applies to it exactly as it does to everything else.
 */
const IDENTITY_LINE = 2.5;

export function pvpIdentityMark(
  role: PvpParticipantRole,
  hullWorldUnits: number,
  visualScale: number,
): PvpIdentityMark {
  const radius = hullWorldUnits * visualScale * IDENTITY_RADIUS;
  const colour = pvpRoleTint(role);
  switch (role) {
    case 'local':
      /*
       * Four arcs on the diagonals, and a tick below the hull.
       *
       * The ship the camera follows is the one a player looks for first when a
       * fight gets crowded, so it carries the densest mark - but not a closed
       * one. The tick is what makes it unmistakable at the smallest drawn size.
       */
      return {
        shape: {
          kind: 'arcs',
          radius,
          sweep: IDENTITY_ARC_SWEEP,
          angles: IDENTITY_ARC_DIAGONALS,
          tick: { radius: radius * IDENTITY_TICK, halfWidth: radius * IDENTITY_TICK_HALF },
        },
        colour,
        lineWidth: IDENTITY_LINE,
        alpha: 0.95,
      };
    case 'ally':
      return {
        shape: {
          kind: 'arcs',
          radius,
          sweep: IDENTITY_ARC_SWEEP,
          // Two, on the horizontal, and no tick: an ally is information, not
          // the thing being aimed.
          angles: IDENTITY_ARC_SIDES,
          tick: null,
        },
        colour,
        lineWidth: IDENTITY_LINE,
        alpha: 0.8,
      };
    default:
      return {
        shape: { kind: 'brackets', radius, arm: radius * IDENTITY_ARM },
        colour,
        lineWidth: IDENTITY_LINE,
        alpha: 0.9,
      };
  }
}

/**
 * Who the local player IS, read out of the frame the room just sent.
 *
 * The scene knows its own participant id from the welcome and has no other way
 * to learn its SIDE - a team is a fact about the roster, and the roster is the
 * frame. So the local ship is looked up by id and its team taken from there.
 *
 * The fallback, when the local ship is not in the frame yet, is a team equal to
 * the id. That is not a guess dressed up as an answer: it is precisely the 1x1,
 * where the id is the seat and the seat is the side, and it makes every other
 * ship an enemy - which is the only honest reading before the authority has
 * described the roster.
 */
export function pvpLocalIdentity(
  frame: PvpRenderFrame,
  localId: PvpParticipantId | null,
): PvpIdentity | null {
  if (localId === null) {
    return null;
  }
  const mine = frame.ships.find((ship) => ship.seat === localId);
  return mine
    ? { participantId: mine.seat, team: mine.team }
    : { participantId: localId, team: localId };
}

/**
 * What a drawn thing is to the person looking at it.
 *
 * Takes the participant's own id and team - never an index, never a position in
 * `frame.ships`. This is the single function the scene asks, for hulls and for
 * projectiles alike, so a shot can never be coloured on a different rule from
 * the ship that fired it.
 */
export function pvpShipRole(
  ship: { readonly seat: PvpParticipantId; readonly team: PvpParticipantId },
  local: PvpIdentity | null,
): PvpParticipantRole {
  return pvpParticipantRole({ participantId: ship.seat, team: ship.team }, local);
}

/* -------------------------------------------------------------------------- */
/*  Runes, drawn outside the ship                                             */
/* -------------------------------------------------------------------------- */

/**
 * What each rune reads as. Colour only; the room owns the effect.
 *
 * ## Derived, and that is the point
 *
 * Two different things spend this - the artefact standing on the pad and the
 * indicator around a ship carrying the effect - and a semantic colour is only
 * worth having if the two agree. So it is not a table of its own any more: it
 * is the `body` step of {@link PVP_RUNE_PALETTES}, which is where the art
 * direction's decision actually lives. One decision, two roles, nothing to
 * drift apart.
 *
 * ## Two of the three changed in gate V25B
 *
 * Speed was green (`0x3cff8a`) and teleport was cyan (`0x3cd2ff`). The approved
 * direction is amber, electric blue and violet - and the old pair had teleport
 * sharing the cyan family with `PVP_OWN_TINT`, the mark around the player's own
 * hull. The new blue is more saturated and darker than that mark, and the
 * artefact's shape - a vector head, not a ring - is what separates them when
 * the colour does not.
 *
 * Double damage did not move: `0xff5a3c` is already the `body` of its palette.
 */
export const PVP_RUNE_TINT: Readonly<Record<PvpRuneWireKind, number>> = Object.freeze({
  double_damage: PVP_RUNE_PALETTES.double_damage.body,
  speed: PVP_RUNE_PALETTES.speed.body,
  teleport: PVP_RUNE_PALETTES.teleport.body,
});

/**
 * The runes that put a persistent mark on a ship, which is not all three.
 *
 * Teleport is not a running effect at all: the frame carries `teleportCharges`, a COUNT
 * of something the player may spend, which the HUD prints as a number. Drawing a
 * permanent ring for it would say a ship is under an effect when it is holding
 * an option.
 *
 * So two, and both because the frame carries a countdown that a person watching
 * the arena needs: "that one is hitting twice as hard" and "that one is
 * faster than you".
 */
export type PvpRuneIndicatorKind = 'double_damage' | 'speed';

/**
 * Which indicator a ship is wearing, from the authority and from nothing else.
 *
 * The one place the question is answered. No timer, no pickup the client thinks
 * it saw, no collision, no completed animation: the frame carries the remaining
 * ticks, and when it stops carrying them the answer is `null` on the very next
 * frame. The precedence is the one the hull tint used to have, so a ship
 * carrying both still reads as the more dangerous of the two.
 */
export function pvpActiveRuneEffect(
  effects: { readonly doubleDamageTicks: number; readonly speedTicks: number } | null | undefined,
): PvpRuneIndicatorKind | null {
  if (!effects) {
    return null;
  }
  if (effects.doubleDamageTicks > 0) {
    return 'double_damage';
  }
  if (effects.speedTicks > 0) {
    return 'speed';
  }
  return null;
}

/**
 * The geometry of an indicator, in world units, around a hull at the origin.
 *
 * Two shapes, deliberately unlike each other AND unlike all three identity
 * marks: the player has to read "whose ship is that" and "what is it carrying"
 * off one frame, and a fourth ring would have made both harder.
 */
export type PvpRuneIndicatorShape =
  /**
   * Filled points, angled outwards. Solid where every identity mark is a
   * stroke, which is what tells the two apart with no colour at all.
   */
  | {
      readonly kind: 'spikes';
      /** Where each point begins and ends, measured from the hull's centre. */
      readonly inner: number;
      readonly outer: number;
      /** Half the base of a point, across the direction it faces. */
      readonly halfWidth: number;
      /** Which way each point faces, in radians. */
      readonly angles: readonly number[];
    }
  /**
   * Open chevrons behind the ship, apexes leading. The only drawn thing in the
   * arena that turns with a hull, which is how it says a direction.
   */
  | {
      readonly kind: 'chevrons';
      /** Half a chevron's span, across the direction of travel. */
      readonly halfSpan: number;
      /**
       * How far the wings trail behind their own apex.
       *
       * Behind, and never in front. An apex that reached forward of `back` would
       * be the innermost point of the whole mark, and the innermost point is the
       * one that has to clear the team ring - so a chevron drawn the other way
       * round put a rune cue inside an identity cue at 1x. It was caught by the
       * arithmetic in `rune-indicator.test.ts` rather than by a screenshot.
       */
      readonly sweep: number;
      /** How far behind the centre each chevron's APEX sits. */
      readonly backs: readonly number[];
    };

export interface PvpRuneIndicator {
  readonly shape: PvpRuneIndicatorShape;
  readonly colour: number;
  readonly lineWidth: number;
  /**
   * How far the furthest drawn point reaches, in world units.
   *
   * Stated by the factory rather than recomputed by every caller: it is what
   * {@link pvpRuneIndicatorScale} turns into a projected size, and a second
   * derivation of "how big is this" is a second chance to get it wrong.
   */
  readonly reach: number;
}

/**
 * How the pulse breathes, per rune. Decoration, and bounded on purpose.
 *
 * The pulse is the ONE thing here a local clock is allowed to drive, and only
 * while the authority still says the effect is running. It cannot decide when an
 * effect ends, it never reaches zero - an indicator that blinked out would read
 * as an effect that had expired - and the period is slow enough that six of them
 * on screen is a breath rather than a flicker.
 */
export const PVP_RUNE_PULSE: Readonly<
  Record<PvpRuneIndicatorKind, { readonly low: number; readonly high: number; readonly ms: number }>
> = Object.freeze({
  // Short and insistent: the aggressive one.
  double_damage: Object.freeze({ low: 0.72, high: 1, ms: 900 }),
  // Longer and quieter: speed is stated by the shape, not by the brightness.
  speed: Object.freeze({ low: 0.68, high: 0.94, ms: 1_400 }),
});

/**
 * The indicator's alpha at an instant. A number, so a frame allocates nothing.
 *
 * `nowMs` is the scene's own clock and appears nowhere else: it moves an alpha
 * between two bounds and can change no other thing about what is drawn.
 */
export function pvpRunePulseAlpha(kind: PvpRuneIndicatorKind, nowMs: number): number {
  const pulse = PVP_RUNE_PULSE[kind];
  // A cosine rather than a sine so the brightest instant is the one the effect
  // starts on, which is when a player is looking for it.
  const phase = (Math.cos((nowMs / pulse.ms) * Math.PI * 2) + 1) / 2;
  return pulse.low + (pulse.high - pulse.low) * phase;
}

/**
 * Whether an indicator means a DIRECTION and must therefore turn with the hull.
 *
 * True for exactly one of them. An identity mark never turns - a bracket that
 * rotated would read as a different shape depending on which way a ship happened
 * to be facing - and the speed chevrons are the deliberate opposite: their whole
 * content is which way the ship is going.
 */
export function pvpRuneTurnsWithHeading(kind: PvpRuneIndicatorKind): boolean {
  return kind === 'speed';
}

/**
 * How far out the indicators sit, as fractions of the hull's DIAMETER.
 *
 * Every one of them is outside `IDENTITY_RADIUS` (0.58), so the three cues
 * occupy three bands: the art, then the mark that says whose it is, then the
 * mark that says what it is carrying. `rune-indicator.test.ts` asserts the
 * clearance at every drawn size AND at both ends of the projected-scale range,
 * rather than describing it.
 */
const RUNE_SPIKE_INNER = 0.66;
const RUNE_SPIKE_OUTER = 1.05;
const RUNE_SPIKE_HALF_WIDTH = 0.115;
/**
 * The diagonals, and the reason the points may reach past the bar's lift.
 *
 * The bar is a 70-wide rectangle floating straight above the hull. A point on a
 * diagonal at radius R sits at (0.707R, -0.707R): to reach the bar's height it
 * needs 0.707R >= lift - height/2, and by then its x is that same 0.707R, which
 * is past the bar's own half-width. The two can never occupy a pixel, at any
 * radius and any drawn size - which is what lets this mark grow when the world
 * shrinks. The test checks it as a rectangle intersection rather than trusting
 * the sentence.
 */
const RUNE_SPIKE_ANGLES: readonly number[] = Object.freeze([
  Math.PI / 4,
  (3 * Math.PI) / 4,
  (5 * Math.PI) / 4,
  (7 * Math.PI) / 4,
]);
/*
 * There is no outline joining the four points, and three captures are why.
 *
 * The annulus this mark lives in is thin: the team ring's outer edge is at 0.58
 * of the hull's diameter and the health bar floats at 0.88 of it, so any closed
 * shape drawn between them is about as far from the hull as it is wide - and
 * every one of them read as something the arena already uses.
 *
 * - Corners on the diagonals, edges straight: those edges are horizontal and
 *   vertical. That is a SQUARE, and a square around a ship is exactly the four
 *   corner brackets an enemy wears. `gatev1-after3` and `-after4`.
 * - The same square broken into stubs: the stubs merged with the points into
 *   four solid blocks - the same square, drawn worse. `gatev1-after2`.
 * - Corners on the axes - a true diamond - puts its top corner inside the
 *   health bar, which is the one thing nothing may cross.
 * - A concave star between the two: with the valleys held outside the team ring
 *   there is not enough depth left for the concavity to show, and it degenerates
 *   into the square again. `gatev1-after5`.
 *
 * So the mark is four DISCONNECTED points, which is a silhouette nothing else in
 * the arena has, and the legibility that an outline was meant to buy comes from
 * the points being bigger instead - in world units here, and in projected pixels
 * through `pvpRuneIndicatorScale`.
 */

/**
 * Two chevrons rather than three, and the band is why.
 *
 * Between the team ring at 0.58 and the health bar's lift there is not room for
 * three of these plus their strokes at 1x, and the thing that would have been
 * given up to fit a third is clearance - either a rune cue crossing an identity
 * cue or one crossing health. Two read as a trail perfectly well; a third would
 * have been decoration bought with the only two constraints that matter.
 */
const RUNE_CHEVRON_HALF_SPAN = 0.2;
const RUNE_CHEVRON_SWEEP = 0.09;
const RUNE_CHEVRON_BACKS: readonly number[] = Object.freeze([0.66, 0.75]);

/**
 * The stroke, which deliberately does NOT grow with the ship - the same rule the
 * identity mark follows, and thicker than it.
 *
 * Four rather than the mark's 2.5, and it was measured rather than chosen: at
 * zoom 0.4 on a 1366-wide viewport a three-unit chevron behind a 1x hull was
 * present in the capture and not legible in it. The indicator is competing with
 * the arena's own glow at that distance, and a cue a player has to look for is
 * not a cue.
 */
const RUNE_LINE = 4;

/**
 * What to draw around a ship carrying a rune, and where.
 *
 * A pure function of the KIND and the size. Nothing about health, aim, tick,
 * projectile or role can reach it, which is what makes "an indicator never
 * repaints the ship and never replaces the team mark" true by construction.
 */
export function pvpRuneIndicator(
  kind: PvpRuneIndicatorKind,
  hullWorldUnits: number,
  visualScale: number,
): PvpRuneIndicator {
  const span = hullWorldUnits * visualScale;
  const colour = PVP_RUNE_TINT[kind];
  if (kind === 'double_damage') {
    return {
      shape: {
        kind: 'spikes',
        inner: span * RUNE_SPIKE_INNER,
        outer: span * RUNE_SPIKE_OUTER,
        halfWidth: span * RUNE_SPIKE_HALF_WIDTH,
        angles: RUNE_SPIKE_ANGLES,
      },
      colour,
      lineWidth: RUNE_LINE,
      // The points, which are the star's own corners.
      reach: span * RUNE_SPIKE_OUTER,
    };
  }
  const halfSpan = span * RUNE_CHEVRON_HALF_SPAN;
  const sweep = span * RUNE_CHEVRON_SWEEP;
  const backs = RUNE_CHEVRON_BACKS.map((back) => span * back);
  return {
    shape: { kind: 'chevrons', halfSpan, sweep, backs },
    colour,
    lineWidth: RUNE_LINE,
    // A wing tip of the furthest chevron, which is off the axis.
    reach: Math.max(...backs.map((back) => Math.hypot(back + sweep, halfSpan))),
  };
}

/* -------------------------------------------------------------------------- */
/*  Holding a rune legible when the world shrinks                             */
/* -------------------------------------------------------------------------- */

/**
 * How large the Double Damage mark must be ON SCREEN, and how large it may be.
 *
 * ## Why a projected size at all
 *
 * Everything else in the arena is a world length: it scales with the ship and
 * with the camera, which is correct for a thing that IS part of the world. This
 * mark is not - it is a statement about a ship, and a statement has to be
 * readable at whatever distance the player chose to stand. At the arena's
 * minimum zoom of 0.4 a 1x hull is 21 px across and the four points were a
 * couple of pixels each: present in the capture, and not a cue in the frame.
 *
 * ## Why the ceiling
 *
 * Because the compensation has to be able to stop. Left unbounded it would keep
 * a fixed pixel size at every zoom, so at maximum zoom the mark would shrink
 * towards a badge stuck on a large ship, which is the opposite mistake.
 *
 * Both are measured from the reach - the furthest drawn point - so they are
 * about the mark's SIZE and not about how much of it happens to be ink.
 */
export const PVP_RUNE_PROJECTED_MIN = 30;
export const PVP_RUNE_PROJECTED_MAX = 72;

/**
 * How far the compensation may go, in both directions. Neither is taste.
 *
 * ## The floor is the team mark
 *
 * Shrinking is what would push a rune cue back into the ring that says whose
 * ship it is. The nearest ink the mark puts down is the ends of the diamond's
 * drawn stretches, stroke included; 0.95 is the smallest factor that keeps that
 * outside the local ship's outer ring at every drawn size, with room to spare.
 *
 * ## The ceiling is the health bar
 *
 * A point's TIP can never touch the bar - it is on a diagonal, and by the height
 * the bar floats at, its x is already past the bar's own half-width. The
 * diamond's top edge is not on a diagonal: it is horizontal, and it walks up
 * into a 70x8 rectangle 46 units above the hull as the mark grows. 1.15 is where
 * it stops, stroke included.
 *
 * ## So the band is narrow, and that is the honest outcome
 *
 * A 1x hull at minimum zoom does not reach {@link PVP_RUNE_PROJECTED_MIN}:
 * health is the one thing in this arena nothing may cross, so when the two
 * constraints disagree the bar wins and the mark is drawn smaller than the floor
 * asked for. What made the difference at that zoom was never the last few pixels
 * of radius anyway - it was the diamond, which turned four specks into one
 * silhouette, and a stroke that grows with the factor instead of staying a
 * world-unit hairline the camera then shrinks to nothing.
 *
 * Both numbers are asserted in `rune-indicator.test.ts` - the second as a
 * rectangle intersection against every point the scene actually draws, at every
 * zoom and every drawn size - rather than trusted from this paragraph.
 */
const RUNE_SCALE_MIN = 0.95;
const RUNE_SCALE_MAX = 1.28;

/**
 * What to multiply a rune indicator's geometry by, given how far away the
 * player is standing.
 *
 * Pure, and the camera enters it HERE and nowhere else. The zoom decides a size
 * and can decide nothing else: not whether the mark exists, not which rune it
 * is, not how long it lasts. Those come from the room, through
 * {@link pvpActiveRuneEffect}, and this function has no way to reach them.
 *
 * The ship's own drawn size enters through `indicator.reach`, which is
 * `hullWorldUnits * visualScale * a fraction` - so a 3x ship is already large on
 * screen and asks for no compensation at all, while a 1x hull at minimum zoom
 * asks for the most.
 */
export function pvpRuneIndicatorScale(
  indicator: PvpRuneIndicator,
  zoom: number,
  minProjected: number,
  maxProjected: number,
): number {
  const projected = indicator.reach * zoom;
  if (!Number.isFinite(projected) || projected <= 0) {
    // A zoom of zero or a degenerate mark: draw it as designed rather than
    // dividing by nothing. Nothing about the effect depends on this answer.
    return 1;
  }
  /*
   * One is the preferred answer, and the bounds only move it when they must:
   * grow if the mark would come out under the minimum, shrink if it would come
   * out over the maximum, and leave the approved proportions alone in between.
   */
  const grown = Math.max(1, minProjected / projected);
  const bounded = Math.min(grown, maxProjected / projected);
  return Math.min(Math.max(bounded, RUNE_SCALE_MIN), RUNE_SCALE_MAX);
}

/**
 * **A barra de vida de quem está do MEU lado** (gate V28H.R).
 *
 * Verde, e a minha própria também: eu sou do meu lado. Ela é uma das duas
 * coisas que dizem de quem é uma nave - a outra é o nickname por cima dela -
 * desde que a cor dos efeitos de carta deixou de o fazer.
 *
 * Um verde escolhido para não ser o do `Speed`: aquele é um estado que passa,
 * este é uma identidade que fica.
 */
export const PVP_BAR_FRIENDLY_TINT = 0x3ddc84;

/** E a de quem está do outro. O mesmo vermelho que a barra sempre teve. */
export const PVP_BAR_ENEMY_TINT = 0xff3b30;
